"use client";

import { use, useContext, useEffect, useLayoutEffect } from "react";
import { runInAction } from "mobx";
import { applySnapshot } from "mobx-keystone";
import { RootStoreContext } from "../context";
import { DataPromiseContext } from "../data-promise-context";
import type { InvoiceStore } from "../../mobx/core/invoice/store.invoice";
import type { MeterStore } from "../../mobx/core/meter/store.meter";
import type { MeterReadingStore } from "../../mobx/core/meter/store.meter-reading";
import type { NudgeStore } from "../../mobx/core/nudge/store.nudge";
import type {
  AutoPayEnrollmentStore,
  PaymentMethodStore,
  PaymentStore,
} from "../../mobx/core/payment/store.payment";
import type { ServiceAccountStore } from "../../mobx/core/service-account/store.service-account";
import type UserModel from "../../mobx/core/user/model.user";
import type { UserContactStore } from "../../mobx/core/user/store.user-contact";
import type GlobalStore from "../../mobx/stores/global/store.global";

const IS_SERVER = typeof window === "undefined";

// Track promises that have already been consumed and applied to the store tree.
// Uses WeakSet so promises can be GC'd once no longer referenced anywhere.
const hydrated = new WeakSet<Promise<unknown>>();

// Promises whose store has already been asked to load its remaining pages.
const filled = new WeakSet<Promise<unknown>>();

/**
 * For stores that hold their whole list (`loadAllPages`): the server-rendered
 * seed is only the first page, so after it's applied, load the rest in the
 * background — once per seed, on every page load and navigation.
 *
 * The request isn't forced, so it reads our DB (no SAP round trip) and isn't
 * held back by any refresh cooldown; live SAP re-syncs stay with the
 * screens' own refresh logic. Errors are swallowed: the seed is already
 * on screen.
 */
function useLoadRemainingPages(
  promise: Promise<unknown> | undefined,
  store: { loadAllPages: boolean; hydrate: () => Promise<unknown> },
): void {
  useEffect(() => {
    if (IS_SERVER || !promise || !store.loadAllPages || filled.has(promise)) {
      return;
    }
    filled.add(promise);
    // One task later, so a screen that also refreshes on visit gets to start
    // first — this fill then joins that load instead of walking every page
    // beside it (measured: 24 invoice requests for 225 rows). `hydrate` makes
    // the join; the delay only decides who starts, so a screen without a
    // refresh just fills a tick later.
    // Deliberately not cleared on unmount: `filled` has already claimed this
    // seed, so cancelling here (StrictMode unmounts and remounts at once)
    // would mean no fill at all. The store is global, so a late fill is fine.
    setTimeout(() => {
      void store.hydrate().catch(() => undefined);
    }, 0);
  }, [promise, store]);
}

function useRoot() {
  const root = useContext(RootStoreContext);
  if (!root) {
    throw new Error("useRoot must be used within a RootStoreProvider");
  }
  return root;
}

/**
 * Apply the resolved promise data to the store tree.
 *
 * Mutation is always deferred to `useLayoutEffect` (which runs after the
 * current render commits but before paint). Mutating the MobX observable
 * during render fires reactions synchronously, which tries to schedule
 * updates on already-subscribed observer components and trips React's
 * "cannot update a component while rendering another" check.
 *
 * Consequence: the SSR HTML renders with empty stores and components
 * show their empty/loading states. Client hydrates against the same
 * empty state (no hydration mismatch). The layout effect then populates
 * the store and observers re-render with data — all before the browser
 * paints on modern React, so there is no visible empty-state flash
 * during normal initial loads.
 *
 * `use(promise)` remains in render because it is the Suspense primitive —
 * React needs to see the promise synchronously during render to suspend.
 */
function useHydrateInto<T>(
  promise: Promise<T> | undefined,
  apply: (data: T) => void,
): void {
  const data = promise && !hydrated.has(promise) ? use(promise) : undefined;

  useLayoutEffect(() => {
    if (IS_SERVER) return;
    if (data !== undefined && promise && !hydrated.has(promise)) {
      runInAction(() => {
        apply(data);
      });
      hydrated.add(promise);
    }
    // `apply` closes over `store`/`model` refs that are stable across renders
    // of this component, so it's safe to leave out of deps.
  }, [data, promise, apply]);
}

export function useHydrateInvoices(): InvoiceStore {
  const { invoices: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.invoices;
  // Whether a list is held whole or scrolled a page at a time is the app's
  // call, not the store's: web search, the open-balance total and the bills
  // the Pay flow offers all run over `data` in memory, so a partial list is
  // wrong rather than merely short. Mobile scrolls invoices incrementally and
  // leaves this off. Set before the seed below, which reads it to decide
  // whether to merge. Idempotent, so a re-render costs nothing.
  store.loadAllPages = true;
  useHydrateInto(promise, (data) => {
    // Merges once loaded, so the first-page seed can't shrink the list.
    store.seedRaw(data);
    store.initialized = true;
  });
  useLoadRemainingPages(promise, store);
  return store;
}

export function useHydrateServiceAccounts(): ServiceAccountStore {
  const { serviceAccounts: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.serviceAccounts;
  useHydrateInto(promise, (data) => {
    // Merges once loaded, so the first-page seed can't shrink the list.
    store.seedRaw(data);
    store.initialized = true;
  });
  useLoadRemainingPages(promise, store);
  return store;
}

export function useHydratePayments(): PaymentStore {
  const { payments: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.payments;
  // Held whole on web, as invoices are: payment-history search and the
  // dashboard's last-payment figures run over `data` in memory. Mobile's
  // Payment history scrolls a page at a time and leaves this off.
  store.loadAllPages = true;
  useHydrateInto(promise, (data) => {
    // Merges once loaded, so the first-page seed can't shrink the list.
    store.seedRaw(data);
    store.initialized = true;
  });
  useLoadRemainingPages(promise, store);
  return store;
}

export function useHydratePaymentMethods(): PaymentMethodStore {
  const { paymentMethods: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.paymentMethods;
  useHydrateInto(promise, (data) => {
    store.setRaw(data);
    store.initialized = true;
  });
  return store;
}

export function useHydrateMeters(): MeterStore {
  const { meters: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.meters;
  useHydrateInto(promise, (data) => {
    // Merges once loaded, so the first-page seed can't shrink the list.
    store.seedRaw(data);
    store.initialized = true;
  });
  useLoadRemainingPages(promise, store);
  return store;
}

export function useHydrateMeterReadings(): MeterReadingStore {
  const { meterReadings: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.meterReadings;
  useHydrateInto(promise, (data) => {
    store.setRaw(data);
    store.initialized = true;
  });
  return store;
}

export function useHydrateNotifications(): NudgeStore {
  const { notifications: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.notifications;
  useHydrateInto(promise, (data) => {
    store.setRaw(data);
    store.initialized = true;
  });
  return store;
}

export function useHydrateAutoPayEnrollments(): AutoPayEnrollmentStore {
  const { autoPayEnrollments: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.autoPayEnrollments;
  useHydrateInto(promise, (data) => {
    // Merges once loaded, so the first-page seed can't shrink the list.
    store.seedRaw(data);
    store.initialized = true;
  });
  useLoadRemainingPages(promise, store);
  return store;
}

export function useHydrateUser(): UserModel {
  const { user: promise } = useContext(DataPromiseContext);
  const model = useRoot().tilliX.user;
  useHydrateInto(promise, (data) => {
    if (data) {
      applySnapshot(model, data);
    }
  });
  return model;
}

export function useHydrateContacts(): UserContactStore {
  const { contacts: promise } = useContext(DataPromiseContext);
  const store = useRoot().tilliX.contacts;
  useHydrateInto(promise, (data) => {
    store.setRaw(data);
    store.initialized = true;
  });
  return store;
}

/**
 * Lazily hydrate `globalStore.banks` once per session. Unlike the other
 * `useHydrate*` hooks, banks are not pre-fetched on the server and threaded
 * through `DataPromiseContext` — they're fetched on demand from any client
 * component that needs the bank catalog (e.g. ACH payment-method forms).
 *
 * Repeat calls within the same session are no-ops thanks to the guard on
 * `GlobalStore.hydrateBanks`.
 */
export function useHydrateBanks(): GlobalStore {
  const store = useRoot().global;
  useEffect(() => {
    void store.hydrateBanks();
  }, [store]);
  return store;
}
